import {
  Component,
  inject,
  signal,
  Input,
  OnInit,
  OnChanges,
  HostListener,
  effect,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
} from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { DatePickerModule } from 'primeng/datepicker';
import {
  ReferralFilter,
  ReferralStatus,
  REFERRAL_STATUS_OPTIONS,
} from '@ics-referral-hub/shared-types';
import { ReferralFormService } from '../../services/referral-form.service';

import { Output, EventEmitter } from '@angular/core';
import { Logger } from '../../shared/utils/logger.util';
import { endOfDay, startOfDay } from '../../features/analytics/utils/analytics-date.util';

@Component({
  selector: 'app-filter-dialog',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    MultiSelectModule,
    DatePickerModule,
  ],
  template: `
    <p-dialog
      [(visible)]="visible"
      [modal]="true"
      [style]="{ width: '30rem' }"
      [draggable]="false"
      [resizable]="false"
      [dismissableMask]="true"
      appendTo="body"
      styleClass="modal-themed filter-dialog"
      (onHide)="onDialogHide()"
    >
      <ng-template pTemplate="header">
        <div class="dialog-header-content">
          <span class="dialog-title">Filter</span>
          <button
            pButton
            text
            label="Clear All"
            class="p-button-sm clear-all-header-btn"
            [class.dimmed]="!hasAnyFiltersActive()"
            (click)="clearAll()"
          ></button>
        </div>
      </ng-template>

      <div class="filter-description">
        Enter either one information to filter type of client.
      </div>

      <form [formGroup]="filterForm">
        <div class="filter-form">
          <div class="p-field">
            <label for="referralId">Referral ID</label>
            <input
              id="referralId"
              type="text"
              pInputText
              formControlName="referralId"
              class="p-inputtext-sm"
              style="width: 100%"
            />
          </div>

          <div class="p-field">
            <label for="patientName">Patient Name</label>
            <input
              id="patientName"
              type="text"
              pInputText
              formControlName="patientName"
              class="p-inputtext-sm"
              style="width: 100%"
            />
          </div>

          <div class="p-field">
            <label for="hospitalIds">Hospital Name</label>
            <p-multiselect
              id="hospitalIds"
              [options]="hospitalOptions()"
              formControlName="hospitalIds"
              [placeholder]="
                isLoadingHospitals() ? 'Loading hospitals...' : 'Select hospitals'
              "
              styleClass="p-inputtext-sm"
              [style]="{ width: '100%' }"
              [appendTo]="'body'"
              [display]="'chip'"
            ></p-multiselect>
          </div>

          <div class="p-field">
            <label for="programId">Program Name</label>
            <p-select
              id="programId"
              [options]="programOptions()"
              formControlName="programId"
              [placeholder]="
                isLoadingPrograms() ? 'Loading programs...' : 'Please select'
              "
              [showClear]="true"
              styleClass="p-inputtext-sm"
              [style]="{ width: '100%' }"
              [appendTo]="'body'"
            ></p-select>
          </div>

          <div class="p-field">
            <label for="programType">Program Type</label>
            <p-select
              id="programType"
              [options]="programTypeOptions"
              formControlName="programType"
              placeholder="All Types"
              [showClear]="true"
              styleClass="p-inputtext-sm"
              [style]="{ width: '100%' }"
              [appendTo]="'body'"
            ></p-select>
          </div>

          <div class="p-field">
            <label for="referredStartDate">Referred Start Date</label>
            <p-datepicker
              id="referredStartDate"
              formControlName="referredStartDate"
              [showIcon]="true"
              dateFormat="mm/dd/yy"
              placeholder="MM/DD/YYYY"
              class="p-inputtext-sm"
              [style]="{ width: '90%' }"
              [appendTo]="'body'"
            ></p-datepicker>
          </div>

          <div class="p-field">
            <label for="referredEndDate">Referred End Date</label>
            <p-datepicker
              id="referredEndDate"
              formControlName="referredEndDate"
              [showIcon]="true"
              dateFormat="mm/dd/yy"
              placeholder="MM/DD/YYYY"
              class="p-inputtext-sm"
              [style]="{ width: '90%' }"
              [appendTo]="'body'"
            ></p-datepicker>
          </div>

          <div class="p-field">
            <label for="status">Status</label>
            <p-multiselect
              id="status"
              [options]="statusOptions"
              formControlName="status"
              placeholder="Select Status"
              styleClass="p-inputtext-sm"
              [style]="{ width: '100%' }"
              [appendTo]="'body'"
              [display]="'chip'"
            ></p-multiselect>
          </div>

          <div class="p-field">
            <label for="needsAttention">Attention</label>
            <p-select
              id="needsAttention"
              [options]="needsAttentionOptions"
              formControlName="needsAttention"
              placeholder="All referrals"
              [showClear]="true"
              styleClass="p-inputtext-sm"
              [style]="{ width: '100%' }"
              [appendTo]="'body'"
            ></p-select>
          </div>

          <div class="p-field">
            <label for="archivedFilter">Archived</label>
            <p-select
              id="archivedFilter"
              [options]="archivedFilterOptions"
              formControlName="archivedFilter"
              placeholder="Active only"
              [showClear]="true"
              styleClass="p-inputtext-sm"
              [style]="{ width: '100%' }"
              [appendTo]="'body'"
            ></p-select>
          </div>

          @if (showAssignmentFilter()) {
            <div class="p-field">
              <label for="assignedTo">Assignment</label>
              <p-select
                id="assignedTo"
                [options]="coordinatorOptions()"
                formControlName="assignedTo"
                [placeholder]="
                  isLoadingCoordinators()
                    ? 'Loading coordinators...'
                    : 'Select coordinator'
                "
                [showClear]="true"
                styleClass="p-inputtext-sm"
                [style]="{ width: '100%' }"
                [appendTo]="'body'"
              ></p-select>
            </div>
          }
        </div>
      </form>

      <ng-template pTemplate="footer">
        <div class="dialog-footer">
          <p-button
            label="CANCEL"
            styleClass="btn-outlined"
            (onClick)="cancel()"
          ></p-button>
          <p-button
            label="APPLY"
            styleClass="btn-primary"
            (onClick)="apply()"
          ></p-button>
        </div>
      </ng-template>
    </p-dialog>
  `,
  styles: [
    `
      :host ::ng-deep {
        .filter-dialog {
          .p-dialog-header {
            padding: 1.5rem 1.5rem 1rem;
            background-color: white;
            border-bottom: none;
          }

          .p-dialog-title {
            font-weight: 600;
            color: var(--text-color);
            width: 100%;
          }

          .dialog-header-content {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;

            .dialog-title {
              font-weight: 600;
              font-size: 1.125rem;
              color: var(--text-color);
            }

            .clear-all-header-btn {
              font-size: 0.8rem;
              font-weight: 600;
              color: var(--accent-color);
              transition: opacity 0.2s;

              &.dimmed {
                opacity: 0.35;
                pointer-events: none;
              }
            }
          }

          .p-dialog-content {
            padding: 0 1.5rem 1.5rem;
            background-color: white;
          }

          .p-dialog-footer {
            padding: 1rem 1.5rem 1.5rem;
            border-top: 1px solid var(--border-color);
            background-color: white;
          }

          .p-inputtext {
            padding: 0.5rem 0.75rem;
          }

          .p-select-label,
          .p-multiselect-label {
            padding: 0.5rem 0.75rem;
          }
        }

        ::ng-deep .p-button-text {
          color: rgba(79, 83, 82, 0.92) !important; // or your desired color
        }

      }

      .filter-description {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 1.5rem;
        color: var(--text-muted);
        font-size: 0.9rem;

        .clear-buttons {
          display: flex;
          align-items: center;
        }
      }

      .filter-form {
        .p-field {
          margin-bottom: 1.5rem;

          label {
            display: block;
            margin-bottom: 0.5rem;
            font-weight: 500;
            color: var(--text-color);
          }
        }
      }

      .dialog-footer {
        display: flex;
        justify-content: space-between;
        width: 100%;
      }
    `,
  ],
})
export class FilterDialogComponent implements OnInit, OnChanges {
  @HostListener('document:keydown.escape', ['$event'])
  onKeydownHandler(event: Event) {
    if (this.visible) {
      this.cancel();
    }
  }

  @Output() closed = new EventEmitter<void>();
  @Output() filtersApplied = new EventEmitter<ReferralFilter>();
  private fb = inject(FormBuilder);
  private referralFormService = inject(ReferralFormService);
  private filterOptionsLoaded = false;

  @Input() visible = false;
  @Input() currentFilters: ReferralFilter = {};

  hospitalOptions = signal<{ label: string; value: string }[]>([]);
  programOptions = signal<{ label: string; value: string }[]>([]);
  coordinatorOptions = signal<{ label: string; value: string }[]>([]);
  showAssignmentFilter = signal(false);
  isLoadingHospitals = signal(false);
  isLoadingPrograms = signal(false);
  isLoadingCoordinators = signal(false);

  constructor() {
    // Manage hospitalIds control disabled state based on loading
    effect(() => {
      const hospitalIdsControl = this.filterForm.get('hospitalIds');
      if (hospitalIdsControl) {
        if (this.isLoadingHospitals()) {
          hospitalIdsControl.disable({ emitEvent: false });
        } else {
          hospitalIdsControl.enable({ emitEvent: false });
        }
      }
    });

    // Manage programId control disabled state based on loading
    effect(() => {
      const programIdControl = this.filterForm.get('programId');
      if (programIdControl) {
        if (this.isLoadingPrograms()) {
          programIdControl.disable({ emitEvent: false });
        } else {
          programIdControl.enable({ emitEvent: false });
        }
      }
    });

    effect(() => {
      const assignmentControl = this.filterForm.get('assignedTo');
      if (assignmentControl) {
        if (this.isLoadingCoordinators()) {
          assignmentControl.disable({ emitEvent: false });
        } else {
          assignmentControl.enable({ emitEvent: false });
        }
      }
    });
  }

  ngOnInit() {
    this.loadFilterOptionsIfNeeded();
    this.populateFormWithCurrentFilters();
  }

  ngOnChanges() {
    this.loadFilterOptionsIfNeeded();

    // Update form when currentFilters input changes
    if (this.currentFilters) {
      this.populateFormWithCurrentFilters();
    }
  }

  private loadFilterOptionsIfNeeded(): void {
    if (
      !this.visible ||
      this.filterOptionsLoaded ||
      this.isLoadingHospitals() ||
      this.isLoadingPrograms()
    ) {
      return;
    }

    void this.loadFilterOptions();
  }

  private populateFormWithCurrentFilters(): void {
    if (!this.currentFilters) return;

    const status = this.currentFilters.status || [];

    // Resolve hospitalIds: prefer the new array field; fall back to the legacy
    // single-value field so that saved/persisted filters still populate correctly.
    const hospitalIds: string[] =
      this.currentFilters.hospitalIds ??
      (this.currentFilters.hospitalId ? [this.currentFilters.hospitalId] : []);

    // Update form with current filter values
    const formValue: any = {
      referralId: this.currentFilters.referralId || '',
      patientName: this.currentFilters.patientName || '',
      hospitalIds,
      programId: this.currentFilters.programId || null,
      programType: this.currentFilters.programType || null,
      referredStartDate: this.currentFilters.startDate
        ? new Date(this.currentFilters.startDate)
        : null,
      referredEndDate: this.currentFilters.endDate
        ? new Date(this.currentFilters.endDate)
        : null,
      status,
      needsAttention: this.currentFilters.needsAttention || null,
      archivedFilter: this.currentFilters.archivedFilter || null,
      assignedTo: this.currentFilters.assignedTo || null,
    };
    this.filterForm.patchValue(formValue);
  }

  private async loadFilterOptions(): Promise<void> {
    this.isLoadingHospitals.set(true);
    this.isLoadingPrograms.set(true);
    this.isLoadingCoordinators.set(true);

    try {
      const result = await this.referralFormService.getFilterOptions();

      if (!result.success || !result.data) {
        throw new Error(result.error || 'Failed to load filter options');
      }

      this.hospitalOptions.set(
        result.data.hospitals.map((hospital) => ({
          label: hospital.name,
          value: hospital.id,
        })),
      );
      this.programOptions.set(
        result.data.programs.map((program) => ({
          label: program.name,
          value: program.id,
        })),
      );
      this.showAssignmentFilter.set(!!result.data.showAssignmentFilter);
      this.coordinatorOptions.set(
        (result.data.coordinators ?? []).map((coordinator) => ({
          label: coordinator.label,
          value: coordinator.id,
        })),
      );

      this.filterOptionsLoaded = true;
    } catch (error) {
      Logger.error('Error loading filter options:', error);
    } finally {
      this.isLoadingHospitals.set(false);
      this.isLoadingPrograms.set(false);
      this.isLoadingCoordinators.set(false);
    }
  }

  programTypeOptions = [
    { label: 'TCU', value: 'TCU' },
    { label: 'Community', value: 'Community' },
  ];

  needsAttentionOptions = [
    { label: 'Needs attention only', value: true },
  ];

  archivedFilterOptions = [
    { label: 'Active only', value: 'active' },
    { label: 'Archived only', value: 'archived' },
    { label: 'Show all', value: 'all' },
  ];

  statusOptions = REFERRAL_STATUS_OPTIONS.map(({ label, value }) => ({
    label,
    value,
  }));

  filterForm = this.fb.group({
    referralId: [''],
    patientName: [''],
    hospitalIds: [[] as string[]],
    programId: [null],
    programType: [null as 'TCU' | 'Community' | null],
    referredStartDate: [null],
    referredEndDate: [null],
    status: [[] as ReferralStatus[]],
    needsAttention: [null as boolean | null],
    archivedFilter: [null as 'active' | 'archived' | 'all' | null],
    assignedTo: [null as string | null],
  });

  clearAll(): void {
    this.filterForm.reset({
      referralId: '',
      patientName: '',
      hospitalIds: [],
      programId: null,
      programType: null,
      referredStartDate: null,
      referredEndDate: null,
      status: [],
      needsAttention: null,
      archivedFilter: null,
      assignedTo: null,
    });
  }

  hasAnyFiltersActive(): boolean {
    const f = this.currentFilters;
    return !!(
      f.status?.length ||
      f.referralId ||
      f.patientName ||
      f.hospitalIds?.length ||
      f.hospitalId ||
      f.programId ||
      f.programType ||
      f.startDate ||
      f.endDate ||
      f.archivedFilter ||
      f.assignedTo ||
      f.needsAttention
    );
  }

  onDialogHide() {
    this.closed.emit();
  }

  cancel(): void {
    this.visible = false;
    this.closed.emit();
  }

  apply(): void {
    const formValue = this.filterForm.value;
    const statusValues = formValue.status || [];
    const hospitalIdsValue = formValue.hospitalIds || [];

    const filters: ReferralFilter = {
      referralId: formValue.referralId || undefined,
      patientName: formValue.patientName || undefined,
      hospitalIds: hospitalIdsValue.length > 0 ? hospitalIdsValue : undefined,
      programId: formValue.programId || undefined,
      programType: formValue.programType || undefined,
      startDate: formValue.referredStartDate
        ? startOfDay(formValue.referredStartDate)
        : undefined,
      // The picker yields local midnight; the API compares with $lte, so send
      // the end of the picked day or the To day itself is excluded (MYB-5190).
      endDate: formValue.referredEndDate
        ? endOfDay(formValue.referredEndDate)
        : undefined,
      status: statusValues.length > 0 ? statusValues : undefined,
      needsAttention: formValue.needsAttention || undefined,
      archivedFilter: formValue.archivedFilter || undefined,
      assignedTo: formValue.assignedTo || undefined,
    };

    this.filtersApplied.emit(filters);

    this.visible = false;
    this.closed.emit();
  }
}
